import React from "react";
import { DeepPartial, UnpackNestedValue } from "react-hook-form";
import { useMediaPredicate } from "react-media-hook";

import { allCountries } from "./hooks/all-countries";
import { AnswerType, QuestionType } from "components/Question/types";
import { ButtonStyle, ButtonTarget } from "components/Button/constants";
import { enquiryOptions } from "./enquiryOptions";
import { Model } from "./model";
import { OptionType } from "components/Inputs/Select";
import { useSendFormData } from "modules/sendFormData";
import * as Button from "components/Button";
import * as Hyperlink from "components/Hyperlink";
import Questions from "components/Questions";
import Question from "components/Question";
import RecaptchaMessage from "components/RecaptchaMessage";
import RichText from "components/RichText";
import yup from "modules/validation";

type ComponentProps = {
  defaultValues: UnpackNestedValue<DeepPartial<Model>>;
  formType: string;
  model: Record<string, QuestionType<Model>>;
  schema?: yup.ObjectSchema;
  title?: string;
  description?: string;
};

function ContactQuestions({ defaultValues, formType, model, schema, title, description }: ComponentProps) {
  const isTablet = useMediaPredicate("(max-width: 1024px)");
  const { handleSubmit, isSubmitting } = useSendFormData({ formType });
  return (
    <Questions defaultValues={defaultValues} id={formType} onSubmit={handleSubmit} schema={schema}>
      {({ control, isFormSubmitting }) => (
        <>
          <div>
            <h2>{title}</h2>
            {description && <RichText content={description} />}
          </div>
          <div>
            <Question
              control={control}
              label="Your full name*"
              question={model.fullName}
              isSubmitting={isFormSubmitting || isSubmitting}
            />
            <Question
              control={control}
              label="Email*"
              question={model.email}
              isSubmitting={isFormSubmitting || isSubmitting}
            />
          </div>
          <div>
            <Question
              control={control}
              isSubmitting={isFormSubmitting || isSubmitting}
              label="Organization"
              question={model.organizationName}
            />
            <Question
              control={control}
              isSubmitting={isFormSubmitting || isSubmitting}
              label="Reason for enquiry*"
              options={enquiryOptions}
              question={model.reasonForEnquiry}
              type={AnswerType.SELECT}
            />
          </div>
          <div>
            <Question
              control={control}
              isSubmitting={isFormSubmitting || isSubmitting}
              label="Job title"
              question={model.jobTitle}
            />
            <Question
              control={control}
              isSubmitting={isFormSubmitting || isSubmitting}
              label="Candidate number (if known)"
              question={model.candidateNumber}
            />
          </div>
          <div>
            <div>
              <Question
                control={control}
                isSubmitting={isFormSubmitting || isSubmitting}
                label="Country*"
                options={allCountries as OptionType[]}
                question={model.country}
                type={AnswerType.SELECT}
              />
              <Question
                control={control}
                isSubmitting={isFormSubmitting || isSubmitting}
                label="Tel. no"
                question={model.telephoneNumber}
              />
            </div>
            <Question
              control={control}
              isSubmitting={isFormSubmitting || isSubmitting}
              label="Description of enquiry*"
              question={model.descriptionOfEnquiry}
              rows={isTablet ? 6 : 4}
              type={AnswerType.TEXTAREA}
            />
          </div>
          <Question
            control={control}
            isSubmitting={isFormSubmitting || isSubmitting}
            label={`I agree to the Axelos.com`}
            question={model.consent}
            type={AnswerType.CHECKBOX}
          >
            &nbsp;
            <Hyperlink.CustomLink target={ButtonTarget.BLANK} href="/privacy/">
              Privacy Policy*
            </Hyperlink.CustomLink>
          </Question>
          <div>
            <Button.Submit
              disabled={isFormSubmitting || isSubmitting}
              label="Send"
              isSubmitting={isFormSubmitting || isSubmitting}
              style={ButtonStyle.BRAND_BERRY}
            />
            <RecaptchaMessage />
          </div>
        </>
      )}
    </Questions>
  );
}

export default React.memo(ContactQuestions) as typeof ContactQuestions;
